import { CleaningServices } from '@mui/icons-material';
import React from 'react';
import {
  AutocompleteInput,
  AutocompleteInputProps,
  Button,
  Create,
  CreateButton,
  Edit,
  Filter,
  Labeled,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  required,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useBasename,
  useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import useCanWrite from '../../hooks/useCanWrite';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import AutocompleteLocationInput from './components/AutocompleteLocationInput';
import AutocompleteStudioInput from './components/AutocompleteStudioInput';
import AutocompleteUserInput from './components/AutocompleteUserInput';
import FeatureGrantScopeField from './components/FeatureGrantScopeField';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyDeleteButton } from './components/WriteOnlyDeleteButton';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const stateChoices = [
  { id: 'enabled', name: 'Enabled' },
  { id: 'disabled', name: 'Disabled' },
];

const scopeTypeChoices = [
  { id: 'client', name: 'Client' },
  { id: 'country', name: 'Country' },
  { id: 'studio', name: 'Studio' },
  { id: 'location', name: 'Location' },
  { id: 'user', name: 'User' },
];

// Links the grant's feature to the Feature show page (via the manyFeatures
// GET_MANY query).
export const FeatureLinkField: React.FC<{ label?: string }> = ({ label }) => (
  <ReferenceField
    source="feature.id"
    reference="Feature"
    label={label}
    link="show"
    sortable={false}
  >
    <TextField source="key" />
  </ReferenceField>
);

// Feature picker for filters/forms. ReferenceInput resolves options + the
// selected label via the features list + manyFeatures queries.
const FeatureSelectInput: React.FC<{
  source: string;
  alwaysOn?: boolean;
  size?: 'small' | 'medium';
  validate?: AutocompleteInputProps['validate'];
}> = ({ source, size, validate }) => (
  <ReferenceInput source={source} reference="Feature">
    <AutocompleteInput
      label="Feature"
      optionText="key"
      size={size}
      validate={validate}
    />
  </ReferenceInput>
);

// scopeId is polymorphic; render the picker that matches scopeType, scoped to
// the client. Client scope has no scopeId (backend pins it to the clientId).
const ScopeIdField: React.FC<{ scopeType?: string; clientId?: string }> = ({
  scopeType,
  clientId,
}) => {
  switch (scopeType) {
    case 'user':
      return clientId ? (
        <AutocompleteUserInput
          source="scopeId"
          label="User"
          filter={{ clientId }}
          inputProps={{ validate: required() }}
        />
      ) : (
        <AutocompleteInput
          source="scopeId"
          label="User"
          choices={[]}
          disabled
          helperText="Please select a client first"
        />
      );
    case 'studio':
      return clientId ? (
        <AutocompleteStudioInput
          source="scopeId"
          label="Studio"
          filter={{ clientId }}
          isRequired
        />
      ) : (
        <AutocompleteInput
          source="scopeId"
          label="Studio"
          choices={[]}
          disabled
          helperText="Please select a client first"
        />
      );
    case 'location':
      return (
        <AutocompleteLocationInput
          source="scopeId"
          label="Location"
          clientId={clientId}
          isRequired
        />
      );
    case 'country':
      return (
        <TextInput
          source="scopeId"
          label="Country code"
          helperText="ISO country code, e.g. US"
          validate={required()}
        />
      );
    default:
      return null;
  }
};

// Create: scopeType + client are being chosen, so watch the form.
const ScopeIdInput: React.FC = () => (
  <ScopeIdField
    scopeType={useWatch({ name: 'scopeType' })}
    clientId={useWatch({ name: 'client.id' })}
  />
);

// Edit: identity is immutable, so drive the picker from the record.
const EditScopeIdInput: React.FC = () => {
  const record = useRecordContext();
  return (
    <ScopeIdField scopeType={record?.scopeType} clientId={record?.client?.id} />
  );
};

const FeatureGrantFilters: React.FC = (props) => (
  <Filter {...props}>
    <AutocompleteClientInput
      source="clientId"
      label="Client"
      size="small"
      alwaysOn
    />
    <FeatureSelectInput source="featureId" size="small" alwaysOn />
    <SelectInput
      source="scopeType"
      label="Scope"
      choices={scopeTypeChoices}
      size="small"
      margin="none"
      alwaysOn
    />
  </Filter>
);

// Opens the cleanup page, carrying the client along when the caller knows it
// so nobody has to pick it again. The basename has to be explicit: the router
// itself has none, so a bare path would land outside the admin and redirect.
const CleanupLinkButton: React.FC<{ clientId?: string }> = ({ clientId }) => {
  const basename = useBasename();
  const query = clientId
    ? `?${new URLSearchParams({ clientId }).toString()}`
    : '';
  return (
    <Button
      component={Link}
      to={`${basename}/featureGrantCleanup${query}`}
      label="Find redundant grants"
      startIcon={<CleaningServices />}
    />
  );
};

const FeatureGrantListActions: React.FC = () => (
  <TopToolbar>
    <CleanupLinkButton />
    <CreateButton />
  </TopToolbar>
);

export const FeatureGrantList: React.FC = () => (
  <InfiniteList
    filters={<FeatureGrantFilters />}
    actions={<FeatureGrantListActions />}
  >
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <FeatureLinkField label="Feature" />
      <ReferenceField
        source="client.id"
        reference="Client"
        label="Client"
        link="show"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField source="scopeType" label="Scope" sortable={false} />
      <FeatureGrantScopeField label="Scope Target" />
      <TextField source="state" sortable={false} />
      <ReferenceField
        source="createdBy.id"
        reference="User"
        label="Created By"
        link="show"
        sortable={false}
        emptyText="—"
      >
        <TextField source="email" />
      </ReferenceField>
      <WriteOnlyEditButton />
      <ShowButton />
      <WriteOnlyDeleteButton
        mutationMode="pessimistic"
        confirmTitle="Delete feature grant"
        confirmContent="Are you sure you want to delete this feature grant?"
      />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

// Grants for a single scope target (e.g. one user), embedded as a tab on that
// scope's resource. Scope target column is omitted — it's the record itself.
export const ScopedFeatureGrants: React.FC<{
  scopeType: string;
  scopeId: string;
  clientId?: string;
  storeKey: string;
  // Cleanup analyzes a whole client at once, so it is offered where that reads
  // as the same scope the tab is showing.
  showCleanupLink?: boolean;
  // Deleting happens in place, so anything derived from these grants has to be
  // told to re-resolve. Create and edit leave the page and remount on return.
  onGrantChange?: () => void;
}> = ({
  scopeType,
  scopeId,
  clientId,
  storeKey,
  showCleanupLink,
  onGrantChange,
}) => {
  const canWrite = useCanWrite();
  const location = useLocation();
  // Prefill the create form for this scope. Client scope's scopeId is the
  // clientId (forced server-side), so only pass scopeId for other scopes.
  // redirectTo returns to this scope's tab after creating (default is the
  // standalone grant list).
  const createState = {
    record: {
      scopeType,
      ...(scopeType === 'client' ? {} : { scopeId }),
      ...(clientId ? { client: { id: clientId } } : {}),
    },
    redirectTo: location.pathname,
  };
  return (
    <ResourceContextProvider value="FeatureGrant">
      <InfiniteList
        filter={{ scopeType, scopeId }}
        disableSyncWithLocation
        storeKey={storeKey}
        // Embedded in a scope's page — don't override the page title bar.
        title={false}
        actions={
          canWrite || showCleanupLink ? (
            <TopToolbar>
              {showCleanupLink && <CleanupLinkButton clientId={clientId} />}
              {canWrite && (
                <CreateButton label="Create grant" state={createState} />
              )}
            </TopToolbar>
          ) : (
            false
          )
        }
        empty={false}
      >
        <WriteOnlyDatagrid bulkActionButtons={false}>
          <FeatureLinkField label="Feature" />
          <TextField source="state" sortable={false} />
          <ReferenceField
            source="createdBy.id"
            reference="User"
            label="Created By"
            link="show"
            sortable={false}
            emptyText="—"
          >
            <TextField source="email" />
          </ReferenceField>
          <WriteOnlyEditButton />
          <ShowButton />
          <WriteOnlyDeleteButton
            mutationMode="pessimistic"
            redirect={false}
            confirmTitle="Delete feature grant"
            confirmContent="Are you sure you want to delete this feature grant?"
            // onSettled, not onSuccess — supplying onSuccess would replace
            // ReactAdmin's own notification and list refresh.
            mutationOptions={{ onSettled: onGrantChange }}
          />
        </WriteOnlyDatagrid>
      </InfiniteList>
    </ResourceContextProvider>
  );
};

const FeatureGrantShowActions: React.FC = () => (
  <TopToolbar>
    <WriteOnlyEditButton />
    <WriteOnlyDeleteButton
      mutationMode="pessimistic"
      redirect="list"
      confirmTitle="Delete feature grant"
      confirmContent="Are you sure you want to delete this feature grant?"
    />
  </TopToolbar>
);

export const FeatureGrantShow: React.FC = () => (
  <Show actions={<FeatureGrantShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <FeatureLinkField label="Feature" />
      <ReferenceField
        source="client.id"
        reference="Client"
        label="Client"
        link="show"
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField source="scopeType" label="Scope" />
      <FeatureGrantScopeField label="Scope Target" />
      <TextField source="state" />
      <ReferenceField
        source="createdBy.id"
        reference="User"
        label="Created By"
        link="show"
        emptyText="—"
      >
        <TextField source="email" />
      </ReferenceField>
      <TextField source="createdAt" label="Created" />
      <TextField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

const FeatureGrantToolbar = () => (
  <Toolbar sx={{ justifyContent: 'space-between' }}>
    <SaveButton />
    <WriteOnlyDeleteButton
      mutationMode="pessimistic"
      redirect="list"
      confirmTitle="Delete feature grant"
      confirmContent="Are you sure you want to delete this feature grant?"
    />
  </Toolbar>
);

// Create form fields — feature/client/scope are all chosen here.
const FeatureGrantFormFields: React.FC = () => (
  <>
    <FeatureSelectInput source="feature.id" validate={required()} />
    <AutocompleteClientInput source="client.id" label="Client" isRequired />
    <SelectInput
      source="scopeType"
      label="Scope"
      choices={scopeTypeChoices}
      validate={required()}
    />
    <ScopeIdInput />
    <SelectInput
      source="state"
      choices={stateChoices}
      defaultValue="enabled"
      validate={required()}
    />
  </>
);

export const FeatureGrantEdit: React.FC = () => (
  // Identity (feature/client/scope) is immutable — shown read-only; only the
  // scope target and state are editable.
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm
      toolbar={<FeatureGrantToolbar />}
      sx={{ '& .MuiFormControl-root': { width: 400, maxWidth: '100%' } }}
    >
      <Labeled label="Feature">
        <FeatureLinkField />
      </Labeled>
      <Labeled label="Client">
        <TextField source="client.name" />
      </Labeled>
      <Labeled label="Scope">
        <TextField source="scopeType" />
      </Labeled>
      <EditScopeIdInput />
      <SelectInput
        source="state"
        choices={stateChoices}
        validate={required()}
      />
    </SimpleForm>
  </Edit>
);

export const FeatureGrantCreate: React.FC = () => {
  // When launched from a scope's tab, return there after creating; otherwise
  // fall back to the standalone grant list.
  const redirectTo = useLocation().state?.redirectTo as string | undefined;
  return (
    <Create redirect={redirectTo ?? 'list'}>
      <SimpleForm
        sx={{ '& .MuiFormControl-root': { width: 400, maxWidth: '100%' } }}
      >
        <FeatureGrantFormFields />
      </SimpleForm>
    </Create>
  );
};
